import React, { useState } from "react";
import { Table, Select, Checkbox, Button, Popconfirm, Tooltip } from "antd";
import { DeleteOutlined, PlusCircleOutlined } from "@ant-design/icons";
import "../../styles/fieldMappingTable.css";

const { Option } = Select;

// Strip surrounding double-quotes that some CSV/DB headers carry. Source 1 values
// are stored cleaned; Source 2 values are stored raw — matching the exact option
// values the previous MappingTableComponent / TaskMappingTableComponent produced,
// so the Confirm payload stays byte-identical.
const cleanCol = (v) => (typeof v === "string" ? v.replace(/(^"|"$)/g, "") : v);

const FIELD_TYPES = [
  { value: "TEXT", label: "Text" },
  { value: "DATE", label: "Date" },
  { value: "DATETIME", label: "Date & Time" },
  { value: "NUMERIC", label: "Number" },
];

/**
 * FieldMappingTable — the shared editable field-mapping table used by the Create
 * Task flow, the Master Suite compare step (both via TaskMapFields) and the New
 * Reconciliation wizard (via compare/MapFields).
 *
 * Live editing: cells commit on change — no per-row save/undo. Only ROWS that
 * have BOTH Source 1 and Source 2 are synced upward, so blank / half-filled rows
 * never enter the mapping payload.
 *
 * Contract preserved exactly:
 *   props: initialData, fileHeader, sqlHeader, mappingName, dataSourceOne,
 *          dataSourceTwo, assignDataSource, setIsTableSaved?
 *   row:   { key, mappingName, rowKey, source1, source2, fieldType }
 *   sync:  assignDataSource(completeRows, ["<source1>-<rowKey>", …], true)
 */
function FieldMappingTable(props) {
  const [rows, setRows] = useState(props.initialData || []);

  const syncUp = (next) => {
    const complete = next.filter((r) => r.source1 && r.source2);
    const selectedColumns = complete.map((r) => r.source1 + "-" + r.rowKey);
    props.assignDataSource(complete, selectedColumns, true);
    if (props.setIsTableSaved) props.setIsTableSaved(true);
  };

  const updateRow = (key, field, value) => {
    const next = rows.map((r) => (r.key === key ? { ...r, [field]: value } : r));
    setRows(next);
    syncUp(next);
  };

  const deleteRow = (key) => {
    const next = rows.filter((r) => r.key !== key);
    setRows(next);
    syncUp(next);
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        key: Date.now().toString(),
        mappingName: props.mappingName,
        rowKey: false,
        source1: "",
        source2: "",
        fieldType: "TEXT",
      },
    ]);
  };

  const columns = [
    {
      title: "Unique ID",
      dataIndex: "rowKey",
      key: "rowKey",
      width: 64,
      align: "center",
      render: (_, record) => (
        <Checkbox
          checked={!!record.rowKey}
          onChange={(e) => updateRow(record.key, "rowKey", e.target.checked)}
        />
      ),
    },
    {
      title: props.dataSourceOne || "Source 1",
      dataIndex: "source1",
      key: "source1",
      ellipsis: true,
      render: (_, record) => (
        <Select
          showSearch
          value={record.source1 || undefined}
          placeholder="select column"
          style={{ width: "100%" }}
          onChange={(v) => updateRow(record.key, "source1", v)}
        >
          {(props.fileHeader || []).map((col) => {
            const val = cleanCol(col);
            return (
              <Option key={val} value={val} title={val}>
                {val}
              </Option>
            );
          })}
        </Select>
      ),
    },
    {
      title: props.dataSourceTwo || "Source 2",
      dataIndex: "source2",
      key: "source2",
      ellipsis: true,
      render: (_, record) => (
        <Select
          showSearch
          value={record.source2 || undefined}
          placeholder="select column"
          style={{ width: "100%" }}
          onChange={(v) => updateRow(record.key, "source2", v)}
        >
          {(props.sqlHeader || []).map((col) => (
            <Option key={col} value={col} title={col}>
              {col}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Field Type",
      dataIndex: "fieldType",
      key: "fieldType",
      width: 120,
      render: (_, record) => (
        <Select
          value={record.fieldType || "TEXT"}
          style={{ width: "100%" }}
          onChange={(v) => updateRow(record.key, "fieldType", v)}
        >
          {FIELD_TYPES.map((t) => (
            <Option key={t.value} value={t.value}>
              {t.label}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "",
      key: "action",
      width: 56,
      align: "center",
      render: (_, record) => (
        <Popconfirm
          title="Delete this row?"
          onConfirm={() => deleteRow(record.key)}
        >
          <Tooltip title="Delete row">
            <Button type="text" danger size="small" icon={<DeleteOutlined />} />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="field-mapping-table">
      <Table
        dataSource={rows}
        columns={columns}
        rowKey="key"
        pagination={false}
        size="small"
        tableLayout="fixed"
      />
      <Button
        onClick={addRow}
        icon={<PlusCircleOutlined />}
        className="fmt-add-row"
      >
        Add mapping row
      </Button>
    </div>
  );
}

export default FieldMappingTable;
